import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLoginPage() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!authLoading && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Merci de remplir les deux champs.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bienvenue !");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Identifiants invalides.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#FAF7F8] flex items-center justify-center px-6 py-12 relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-[36rem] h-[36rem] rounded-full bg-[#E6D6DF]/40 blur-[140px]" />
        <div className="absolute -bottom-24 left-0 w-[32rem] h-[32rem] rounded-full bg-[#3A2E6E]/8 blur-[120px]" />
      </div>

      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-[#5F6170] hover:text-[#2847B8] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au site
        </Link>

        <div className="bg-white rounded-3xl border border-[#DDD7E2] p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(58,46,110,0.2)]">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-full bg-[#F3EEF4] flex items-center justify-center">
              <Lock className="w-4 h-4 text-[#3A2E6E]" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C98FA3] font-medium">
                Espace administration
              </p>
              <h1 className="font-serif-display text-3xl font-light text-[#1E1E2A] leading-tight mt-1">
                Connexion
              </h1>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Email
              </label>
              <Input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="mt-2 bg-[#FAF7F8] border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                disabled={loading}
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                Mot de passe
              </label>
              <Input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 bg-[#FAF7F8] border-[#DDD7E2] focus-visible:ring-[#2847B8] rounded-xl h-11"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-[#2847B8] text-white hover:bg-[#3A2E6E] disabled:opacity-60 transition-all duration-300 rounded-full h-12 font-medium inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Connexion…
                </>
              ) : (
                "Se connecter"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
